import React, { useState, useEffect } from 'react';
import MagicLogoModal from './components/MagicLogoModal';
import { LogoData, Product, Technique, Variant } from './types';

// API calls go through Vite proxy (same origin, no CORS needed)
// The proxy forwards /api/* and /svg/* to localhost:4050
const API_BASE_URL = '';
console.log('API_BASE_URL: using same-origin proxy');

// Default product for testing when no SKU is provided
// Note: imageUrl will be updated with correct API_BASE_URL in useEffect
const defaultProduct: Product = {
  sku: '376-00.005',
  name: 'Metmaxx® thermosbeker "CremaExtensa"',
  imageUrl: '', // Will be set dynamically
  svgWidth: 595,
  svgHeight: 842,
  svgImageX: 209.61357,
  svgImageY: 150.13,
  svgImageWidth: 374.77284,
  svgImageHeight: 823.36,
  techniques: [
    { code: 'DP-360-M', name: 'Digitale print 360°', maxColors: 8, dimensions: '223x50', position: 'midden', centroidX: 383, centroidY: 662, printAreaWidth: 865, printAreaHeight: 194 },
    { code: 'DP-UV8', name: 'UV Digitale print', maxColors: 8, dimensions: '27x45', position: 'midden', centroidX: 383, centroidY: 662, printAreaWidth: 105, printAreaHeight: 175 },
    { code: 'L6-CO2', name: 'CO2 Laser graveren', maxColors: 1, dimensions: '40x6', position: 'boven', centroidX: 390, centroidY: 295, printAreaWidth: 155, printAreaHeight: 23 },
    { code: 'L7XL', name: 'Laser graveren XL', maxColors: 1, dimensions: '50x30', position: 'midden', centroidX: 383, centroidY: 662, printAreaWidth: 194, printAreaHeight: 116 },
    { code: 'T8', name: 'Tampondruk', maxColors: 2, dimensions: '35x25', position: 'midden', centroidX: 383, centroidY: 662, printAreaWidth: 136, printAreaHeight: 97 },
  ],
};

function App() {
  // Get SKU from URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const skuFromUrl = urlParams.get('sku');
  const quantityFromUrl = urlParams.get('quantity') ? parseInt(urlParams.get('quantity')!, 10) : undefined;
  const techniqueFromUrl = urlParams.get('technique') || undefined;
  const modeFromUrl = (urlParams.get('mode') as 'order' | 'configure') || 'order';
  const callbackUrlFromUrl = urlParams.get('callbackUrl') || undefined;
  const customerIdFromUrl = urlParams.get('customer_id') || urlParams.get('customerId') || undefined;
  const basketIdFromUrl = urlParams.get('basket_id') || urlParams.get('basketId') || undefined;

  // If SKU in URL, modal is always open (no button page)
  const [isModalOpen, setIsModalOpen] = useState(!!skuFromUrl);
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch product data from API when SKU changes
  useEffect(() => {
    async function fetchProductData() {
      if (!skuFromUrl) {
        // No SKU provided, use default - fetch 3D model and variants
        try {
          const baseSku = defaultProduct.sku.split('.')[0];
          const [products3dResponse, variantsResponse] = await Promise.all([
            fetch(`${API_BASE_URL}/api/3d/products/base/${baseSku}`),
            fetch(`${API_BASE_URL}/api/variants/${baseSku}`).catch(() => null),
          ]);

          const [products3dData, variantsData] = await Promise.all([
            products3dResponse.json(),
            variantsResponse?.json().catch(() => null) || Promise.resolve(null),
          ]);

          console.log('Default product 3D data:', products3dData);
          console.log('Default product variants:', variantsData);

          // API returns array for base SKU, get first item
          const product3d = products3dData.success && products3dData.data?.length > 0 ? products3dData.data[0] : null;
          const has3DModel = product3d && (product3d.glbPath || product3d.glb_path);
          console.log('Default product has3DModel:', has3DModel);

          // Process variants
          let variants: Variant[] = [];
          if (variantsData?.success && variantsData?.data) {
            variants = variantsData.data;
            console.log('Default product variants loaded:', variants.length);
          }

          const defaultWithUrl = {
            ...defaultProduct,
            imageUrl: `${API_BASE_URL}/svg/spranz/${defaultProduct.sku}.svg`,
            has3DModel,
            glbPath: has3DModel ? (product3d.glbPath || product3d.glb_path) : undefined,
            variants,
          };
          setProduct(defaultWithUrl);
        } catch (err) {
          console.error('Error loading default product data:', err);
          const defaultWithUrl = {
            ...defaultProduct,
            imageUrl: `${API_BASE_URL}/svg/spranz/${defaultProduct.sku}.svg`,
          };
          setProduct(defaultWithUrl);
        }
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Extract base SKU (e.g., "376-00.005" -> "376-00")
        const baseSku = skuFromUrl.split('.')[0];
        console.log('SKU from URL:', skuFromUrl);
        console.log('Base SKU for API:', baseSku);

        // Fetch all data in parallel for better performance
        const [techniquesResponse, configResponse, products3dResponse, variantsResponse, productResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/techniques/${baseSku}`),
          fetch(`${API_BASE_URL}/api/3d/config/${skuFromUrl}`),
          fetch(`${API_BASE_URL}/api/3d/products/base/${baseSku}`),
          fetch(`${API_BASE_URL}/api/variants/${baseSku}`).catch(() => null),
          fetch(`${API_BASE_URL}/api/products/${skuFromUrl}`).catch(() => null),
        ]);

        // Parse all responses in parallel
        const [techniquesData, configData, products3dData, variantsData, productData] = await Promise.all([
          techniquesResponse.json(),
          configResponse.json(),
          products3dResponse.json(),
          variantsResponse?.json().catch(() => null) || Promise.resolve(null),
          productResponse?.json().catch(() => null) || Promise.resolve(null),
        ]);

        // Get SVG path from product data
        console.log('Raw productData:', productData);
        const svgPath = productData?.success && productData?.data?.svgPath
          ? productData.data.svgPath
          : 'svg/spranz/';
        console.log('Product SVG path:', svgPath);
        console.log('Final imageUrl will be:', `${API_BASE_URL}/${svgPath.replace(/^\//, '')}${skuFromUrl}.svg`);

        console.log('Techniques data:', techniquesData);
        console.log('3D Products data:', products3dData);

        // API returns array for base SKU, get first item
        const product3d = products3dData.success && products3dData.data?.length > 0 ? products3dData.data[0] : null;
        const has3DModel = product3d && (product3d.glbPath || product3d.glb_path);
        console.log('has3DModel:', has3DModel, 'glbPath:', product3d?.glbPath);
        const glbPath = has3DModel ? (product3d.glbPath || product3d.glb_path) : undefined;

        // Process variants
        let variants: Variant[] = [];
        console.log('[App] Raw variantsData:', JSON.stringify(variantsData));
        if (variantsData?.success && variantsData?.data) {
          variants = variantsData.data;
          console.log('[App] Variants loaded:', variants.length, 'variants:', JSON.stringify(variants));
        } else {
          console.log('[App] No variants found - success:', variantsData?.success, 'data:', variantsData?.data);
        }

        // Build techniques array with print area data
        const techniques: Technique[] = [];
        if (techniquesData.success && techniquesData.data) {
          techniquesData.data.forEach((tech: any) => {
            // Get print area from printAreas array (first position)
            const printArea = tech.printAreas?.[0] || {};
            techniques.push({
              code: tech.techniqueCode,
              name: tech.techniqueName || tech.techniqueCode,
              maxColors: tech.colors_max || tech.maxColors || 4,
              dimensions: tech.printArea || '50x50',
              position: tech.position || 'midden',
              centroidX: printArea.x,
              centroidY: printArea.y,
              printAreaWidth: printArea.width,
              printAreaHeight: printArea.height,
              rotation: tech.rotation || 0,
            });
          });
        }

        // Get SVG bounds from config - defaults depend on brand
        // xdconnects uses 1500x1000, spranz uses 595x842 (A4)
        const isXdconnects = svgPath.includes('xdconnect');
        let svgImageX = isXdconnects ? 0 : 209.61357;
        let svgViewboxWidth = isXdconnects ? 1500 : 595;
        let svgViewboxHeight = isXdconnects ? 1000 : 842;
        let svgImageY = isXdconnects ? 0 : 150.13;
        let svgImageWidth = isXdconnects ? 1500 : 374.77284;
        let svgImageHeight = isXdconnects ? 1000 : 823.36;

        if (configData.success && configData.data) {
          svgImageX = configData.data.svgImageX ?? svgImageX;
          svgImageY = configData.data.svgImageY ?? svgImageY;
          svgImageWidth = configData.data.svgImageWidth ?? svgImageWidth;
          svgImageHeight = configData.data.svgImageHeight ?? svgImageHeight;
          svgViewboxWidth = parseFloat(configData.data.svgViewboxWidth) || svgViewboxWidth;
          svgViewboxHeight = parseFloat(configData.data.svgViewboxHeight) || svgViewboxHeight;
        }

        console.log('SVG config:', { isXdconnects, svgPath, svgViewboxWidth, svgViewboxHeight });

        // Build product object
        const loadedProduct: Product = {
          sku: skuFromUrl,
          name: productData?.data?.name || configData.data?.product_name || skuFromUrl,
          imageUrl: `${API_BASE_URL}/${svgPath.replace(/^\//, '')}${skuFromUrl}.svg`,
          svgWidth: svgViewboxWidth,
          svgHeight: svgViewboxHeight,
          svgImageX,
          svgImageY,
          svgImageWidth,
          svgImageHeight,
          techniques: techniques,
          has3DModel,
          glbPath,
          variants,
          svgPath, // Store for variant changes
        };

        console.log('[App] Setting loadedProduct with variants:', loadedProduct.variants?.length || 0);
        setProduct(loadedProduct);
        setIsLoading(false);

        // Auto-open modal when product is loaded from URL
        setIsModalOpen(true);
      } catch (err) {
        console.error('Error fetching product data:', err);
        setError(`Kon productgegevens niet laden voor SKU: ${skuFromUrl}`);
        // Don't fall back to default - keep product null so user sees error
        setIsLoading(false);
      }
    }

    fetchProductData();
  }, [skuFromUrl]);

  const handleSave = async (logoData: LogoData) => {
    console.log('Logo saved:', logoData);

    // Check if we're in an iframe (embedded in Brinxx or other shop)
    const isInIframe = window.self !== window.top;

    if (isInIframe && product) {
      try {
        // Generate transaction ID
        const transactionId = `TXN-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        // Upload logo to server and get permanent URL
        let logoUrl = '';
        let screenshotUrl = '';

        if (logoData.imageData) {
          // Upload the logo image using /api/logos/cache (accepts base64)
          const uploadResponse = await fetch('/api/logos/cache', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              base64Data: logoData.imageData,
              filename: `logo_${transactionId}.png`,
              mimeType: 'image/png',
              clientFingerprint: localStorage.getItem('magic_logo_fingerprint') || transactionId,
            }),
          });

          if (uploadResponse.ok) {
            const uploadResult = await uploadResponse.json();
            // Build full URL from the processedPath
            const baseUrl = window.location.origin;
            if (uploadResult.data?.processedPath) {
              logoUrl = `${baseUrl}/uploads/${uploadResult.data.processedPath.replace('logos/', '')}`;
              screenshotUrl = logoUrl;
            } else if (uploadResult.data?.filePath) {
              logoUrl = `${baseUrl}/uploads/${uploadResult.data.filePath.split('/').pop()}`;
              screenshotUrl = logoUrl;
            }
          }

          // Fallback: use base64 data directly
          if (!logoUrl) {
            logoUrl = logoData.imageData;
            screenshotUrl = logoData.imageData;
          }
        }

        // Get URL parameters (quantity, technique, etc.)
        const urlParams = new URLSearchParams(window.location.search);
        const quantity = parseInt(urlParams.get('quantity') || '0') || 100;
        const techniqueCode = urlParams.get('technique') || logoData.technique || '';
        const techniqueName = urlParams.get('techniqueName') || '';
        const printArea = urlParams.get('printArea') || '';

        // Send message to parent window (Brinxx storefront)
        const message = {
          type: 'MAGIC_LOGO_ADD_TO_ORDER',
          transactionId,
          productSku: product.sku,
          variantSku: product.sku,
          techniqueCode,
          techniqueName,
          printArea,
          quantity,
          screenshotUrl,
          pdfUrl: '', // PDF generation can be added later
          logoUrl,
          logoConfig: {
            width: logoData.width,
            height: logoData.height,
            colors: logoData.colors,
            svgData: logoData.svgData,
          },
        };

        // SECURITY: Use document.referrer origin instead of wildcard '*'
        let targetOrigin = '*';
        try {
          if (document.referrer) {
            targetOrigin = new URL(document.referrer).origin;
          }
        } catch (e) {
          console.warn('[postMessage] Could not parse referrer');
        }

        console.log('[MagicLogoModal] Sending MAGIC_LOGO_ADD_TO_ORDER to parent:', message);
        window.parent.postMessage(message, targetOrigin);

      } catch (error) {
        console.error('[MagicLogoModal] Error in handleSave:', error);
        // Still try to send message with base64 data
        let targetOrigin = '*';
        try {
          if (document.referrer) {
            targetOrigin = new URL(document.referrer).origin;
          }
        } catch (e) {}
        window.parent.postMessage({
          type: 'MAGIC_LOGO_ADD_TO_ORDER',
          transactionId: `TXN-${Date.now()}`,
          productSku: product?.sku || skuFromUrl || '',
          quantity: parseInt(new URLSearchParams(window.location.search).get('quantity') || '100'),
          logoUrl: logoData.imageData,
          screenshotUrl: logoData.imageData,
        }, targetOrigin);
      }
    } else {
      // Not in iframe - show success message
      alert('Logo opgeslagen!');
    }
  };

  const handleMagic3D = () => {
    if (!product) return;
    console.log(`Opening Magic 3D for ${product.sku}...`);
    // Use relative /3d/ path for production (works on any host)
    const viewer3dUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? `http://localhost:10051/?sku=${product.sku}`
      : `${window.location.origin}/3d/?sku=${product.sku}`;
    window.open(viewer3dUrl, '_blank');
  };

  // Handle variant selection - update product SKU and image
  const handleVariantChange = (variantCode: string) => {
    if (!product) return;
    console.log('Variant changed to:', variantCode);

    // Use dynamic svgPath from product or fallback to spranz
    const svgPath = (product.svgPath || 'svg/spranz/').replace(/^\//, '');

    // Update product with new variant SKU and image
    setProduct({
      ...product,
      sku: variantCode,
      imageUrl: `${API_BASE_URL}/${svgPath}${variantCode}.svg`,
    });
  };

  // Show loading while fetching product data (only for demo mode without SKU)
  // When SKU is in URL, skip this and show modal's internal loader directly
  if (isLoading && !skuFromUrl) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: '#002B51',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        color: 'white',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>
          <i className="fas fa-cog fa-spin"></i>
        </div>
        <div>Laden...</div>
      </div>
    );
  }

  // If SKU in URL, render only the modal (handles loading state internally)
  // Must be BEFORE the !product check to avoid showing error while loading
  if (skuFromUrl) {
    // Create temporary product while loading
    const displayProduct = product || {
      sku: skuFromUrl,
      name: 'Loading...',
      imageUrl: '',
      svgWidth: 595,
      svgHeight: 842,
      svgImageX: 0,
      svgImageY: 0,
      svgImageWidth: 0,
      svgImageHeight: 0,
      techniques: [],
    };

    console.log('[App] Rendering modal - product:', product ? 'loaded' : 'null', 'displayProduct variants:', displayProduct.variants?.length || 0);

    return (
      <MagicLogoModal
        isOpen={true}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        onMagic3D={handleMagic3D}
        onVariantChange={handleVariantChange}
        product={displayProduct}
        techniques={displayProduct.techniques}
        isProductLoading={isLoading}
        quantity={quantityFromUrl}
        initialTechnique={techniqueFromUrl}
        mode={modeFromUrl}
        callbackUrl={callbackUrlFromUrl}
        customerId={customerIdFromUrl}
        basketId={basketIdFromUrl}
      />
    );
  }

  // No SKU - show demo page with button
  // Safety check - product should exist in demo mode (defaultProduct)
  if (!product) {
    return <div style={{ padding: '20px' }}>Loading demo product...</div>;
  }

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: '#f0f0f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {!isModalOpen && (
        <div style={{ textAlign: 'center' }}>
          {error && (
            <div style={{ marginBottom: '20px', color: '#dc3545', padding: '10px', background: '#fff5f5', borderRadius: '8px' }}>
              {error}
            </div>
          )}
          <div style={{ marginBottom: '20px', color: '#666' }}>
            Product: <strong>{product.name}</strong><br />
            SKU: <strong>{product.sku}</strong>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            style={{
              padding: '20px 40px',
              fontSize: '18px',
              background: '#002B51',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Open Magic Logo Modal
          </button>
        </div>
      )}

      <MagicLogoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        onMagic3D={handleMagic3D}
        onVariantChange={handleVariantChange}
        product={product}
        techniques={product.techniques}
        quantity={quantityFromUrl}
        initialTechnique={techniqueFromUrl}
        mode={modeFromUrl}
        callbackUrl={callbackUrlFromUrl}
        customerId={customerIdFromUrl}
        basketId={basketIdFromUrl}
      />
    </div>
  );
}

export default App;
